/**
 * The signed-in user's avatar, as something an `<img>` can actually render.
 *
 * `GET /v1/me/avatar` needs the bearer token, and a browser attaches no
 * `Authorization` header to an `<img src>` — so the URL cannot simply be handed
 * to the DOM. This hook fetches the bytes through the authenticated client and
 * wraps them in an object URL, which it revokes when the component unmounts or
 * the image changes. Without that revoke, every avatar refresh would leak a
 * blob for the lifetime of the tab.
 *
 * It fetches nothing at all when the principal says there is no avatar
 * (`has_avatar`), so the common case costs zero requests: callers fall back to
 * initials.
 */
import { useEffect, useState } from 'react';

import { useSession } from './session';

export interface AvatarState {
  /** An object URL for the avatar, or `null` when there is none to show. */
  url: string | null;
  /** True while the bytes are in flight — callers show initials meanwhile. */
  loading: boolean;
}

export interface UseAvatarOptions {
  /**
   * Overrides the principal's `has_avatar`. The principal is only re-resolved
   * on `refresh()`, so a screen that has *just* uploaded one knows the truth
   * before the session does; passing it here shows the new picture immediately
   * instead of waiting a round-trip.
   */
  hasAvatar?: boolean;
  /**
   * Bump to force a re-fetch of bytes that changed without the flag changing —
   * replacing a PNG with another PNG leaves `has_avatar` true and the user id
   * the same, so nothing else in the dependency list would move and the old
   * image would stay on screen.
   */
  version?: number;
}

export function useAvatar(options: UseAvatarOptions = {}): AvatarState {
  const { principal, api } = useSession();
  const { hasAvatar: override, version = 0 } = options;
  const hasAvatar = override ?? principal?.has_avatar ?? false;
  // Re-fetch when the account changes, not just when the flag flips: signing in
  // as somebody else must not leave the previous user's face on screen.
  const identity = principal?.user_id ?? null;
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!hasAvatar || identity === null) {
      setUrl(null);
      return;
    }
    const controller = new AbortController();
    let objectUrl: string | null = null;
    setLoading(true);

    void (async () => {
      try {
        const blob = await api.me.avatar.get(controller.signal);
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      } catch {
        // A missing or failed avatar is not an error state worth surfacing —
        // the initials fallback is a complete answer on its own.
        if (!controller.signal.aborted) setUrl(null);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();

    return () => {
      controller.abort();
      if (objectUrl !== null) URL.revokeObjectURL(objectUrl);
    };
  }, [api, hasAvatar, identity, version]);

  return { url, loading };
}
